import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import PropertyCard from '../components/PropertyCard';
import { JsonLd, faqSchema, breadcrumbSchema } from '../components/JsonLd';
import {
    areaBySlug, areaStats, areaIsIndexable, areaTitle, areaDescription, areaAnswer, areaFaq,
    areaName, areaIntro, areaLabels
} from '../config/areas';
import { NAP } from '../config/nap';
import { LANGS, isRtl as rtl, localizedPath } from '../config/langs';
import { ui } from '../config/seoCopy';
import { collectionNode } from '../config/schema';
import { useBuildData } from '../utils/buildData';

/**
 * /area/:slug — one off-plan landing page per district.
 *
 * Built from /data/area/<slug>.json, the record the prerenderer wrote this
 * page's static HTML from: the area (curated or generated from the feed) and
 * its projects. The two can only differ by what changed since the build.
 */
export default function AreaPage() {
    const { slug } = useParams();
    const { t, i18n } = useTranslation();
    const base = (i18n.language || 'ar').split('-')[0];
    const lang = LANGS.includes(base) ? base : 'en';
    const isRtl = rtl(lang);
    const L = areaLabels(lang);
    const U = ui(lang);
    const { state, data } = useBuildData(`area/${encodeURIComponent(slug)}.json`);
    const all = useBuildData('areas.json');

    // A curated area keeps its hand-written intro from the bundle; the build
    // record supplies the generated ones.
    const area = areaBySlug(slug) || data?.area || null;
    const rows = useMemo(() => data?.rows || [], [data]);
    const stats = useMemo(() => areaStats(rows), [rows]);

    if (state === 'loading' || state === 'error') {
        return (
            <div style={{ minHeight: '100vh' }}>
                <Header />
                <main className="container" style={{ padding: '160px 1rem 5rem', color: '#999' }}>{t('projects.loading')}</main>
            </div>
        );
    }
    if (!area) {
        return (
            <div style={{ minHeight: '100vh' }}>
                <SEO title={L.notFound} path={`/area/${slug}`} noindex />
                <Header />
                <main className="container" style={{ padding: '160px 1rem 5rem', textAlign: 'center' }}>
                    <h1 style={{ color: '#fff' }}>{L.notFound}</h1>
                    <Link to="/areas" style={{ color: 'var(--color-gold)' }}>{L.all}</Link>
                </main>
            </div>
        );
    }

    const name = areaName(area, lang);
    const faq = areaFaq(area, stats, lang);
    const intro = areaIntro(area, lang);
    const hasData = stats.liveCount > 0;
    const url = `${NAP.url}${localizedPath(`/area/${area.slug}`, lang)}`;
    const live = new Set(stats.live.map((p) => String(p.id)));
    const price = (r) => Number(r.min_price) || Infinity;
    const ordered = [...rows].sort((a, b) => (live.has(String(b.id)) - live.has(String(a.id))) || price(a) - price(b));
    const others = (all.data || [])
        .filter((a) => a.slug !== area.slug && a.city === area.city)
        .slice(0, 24);
    const crumbs = [{ name: U.home, to: '/' }, { name: U.areas, to: '/areas' }, { name }];

    return (
        <div style={{ minHeight: '100vh' }} dir={isRtl ? 'rtl' : 'ltr'}>
            <SEO
                fullTitle={hasData ? areaTitle(area, stats, lang) : L.h1(area)}
                description={hasData ? areaDescription(area, stats, lang) : (intro || L.h1(area)).slice(0, 158)}
                path={`/area/${area.slug}`}
                noindex={!areaIsIndexable(stats)}
            />
            <JsonLd data={collectionNode({
                url,
                lang,
                name: hasData ? areaTitle(area, stats, lang) : L.h1(area),
                description: areaDescription(area, stats, lang),
                about: { '@type': 'Place', name: area.en, alternateName: area.ar, address: { '@type': 'PostalAddress', addressLocality: area.en, addressRegion: area.city, addressCountry: 'AE' } },
                items: stats.live
            })} />
            {faq.length > 0 && <JsonLd data={faqSchema(faq, { url, lang })} />}
            <JsonLd data={breadcrumbSchema([
                { name: U.home, url: '/' },
                { name: U.areas, url: '/areas' },
                { name, url: `/area/${area.slug}` }
            ])} />

            <Header />

            <style>{`
                .area-main { padding: 140px 1rem 4rem; }
                .area-h1 { font-size: clamp(2rem, 5vw, 3rem); font-weight: 800; color: #fff; line-height: 1.25; margin: 0 0 1rem; }
                .area-answer { background: rgba(212,175,55,.07); border-inline-start: 3px solid var(--color-gold);
                    border-radius: 8px; padding: 1.25rem 1.5rem; color: #eee; line-height: 1.9; font-size: 1.05rem; max-width: 860px; }
                .area-intro { color: #aaa; line-height: 1.9; max-width: 860px; margin: 1.25rem 0 2rem; }
                .area-stats { display: flex; flex-wrap: wrap; gap: .75rem; margin: 1.5rem 0 2.5rem; }
                .area-stat { border: 1px solid rgba(255,215,0,.25); border-radius: 10px; padding: .75rem 1.1rem; min-width: 140px; }
                .area-stat b { display: block; color: var(--color-gold); font-size: 1.25rem; }
                .area-stat span { color: #999; font-size: .85rem; }
                .area-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 2rem; margin-bottom: 3rem; }
                .area-h2 { color: var(--color-gold); font-size: 1.4rem; font-weight: 700; margin: 3rem 0 1.25rem; }
                .area-faq h3 { color: #fff; font-size: 1.05rem; margin: 1.5rem 0 .4rem; }
                .area-faq p { color: #bbb; line-height: 1.9; margin: 0; max-width: 860px; }
                .area-links { display: flex; flex-wrap: wrap; gap: .6rem; }
                .area-links a { border: 1px solid rgba(255,255,255,.12); border-radius: 999px; padding: .45rem 1rem;
                    color: #ddd; text-decoration: none; font-size: .9rem; }
                .area-links a:hover { border-color: var(--color-gold); color: var(--color-gold); }
                @media (max-width: 768px) { .area-grid { grid-template-columns: 1fr; } }
            `}</style>

            <main className="container area-main">
                <Breadcrumbs items={crumbs} />

                <h1 className="area-h1">{L.h1(area)}</h1>

                {hasData && <div className="area-answer"><p style={{ margin: 0 }}>{areaAnswer(area, stats, lang)}</p></div>}
                {intro && <p className="area-intro">{intro}</p>}

                <a href={`https://wa.me/${NAP.whatsapp}`} target="_blank" rel="noopener noreferrer" className="btn-primary"
                    style={{ display: 'inline-block', margin: '0.5rem 0 1rem', textDecoration: 'none' }}>
                    {L.cta(area)}
                </a>

                {hasData && (
                    <div className="area-stats">
                        <div className="area-stat"><b>{stats.liveCount}</b><span>{L.onSale}</span></div>
                        {stats.priceFrom > 0 && (
                            <div className="area-stat"><b>{stats.priceFrom.toLocaleString('en-US')}</b><span>{L.from}</span></div>
                        )}
                        {stats.developers.length > 0 && (
                            <div className="area-stat"><b>{stats.developers.length}</b><span>{L.developers}</span></div>
                        )}
                        {stats.handoverFrom && (
                            <div className="area-stat">
                                <b>{stats.handoverFrom === stats.handoverTo ? stats.handoverFrom : `${stats.handoverFrom}–${stats.handoverTo}`}</b>
                                <span>{L.handover}</span>
                            </div>
                        )}
                    </div>
                )}

                <h2 className="area-h2">{L.list(area, rows.length)}</h2>
                <div className="area-grid">
                    {ordered.map((p) => <PropertyCard key={p.id} project={p} />)}
                </div>

                {faq.length > 0 && (
                    <section className="area-faq">
                        <h2 className="area-h2">{L.faq(area)}</h2>
                        {faq.map((f) => (
                            <div key={f.q}>
                                <h3>{f.q}</h3>
                                <p>{f.a}</p>
                            </div>
                        ))}
                    </section>
                )}

                {others.length > 0 && (
                    <>
                        <h2 className="area-h2">{L.others}</h2>
                        <div className="area-links">
                            {others.map((a) => (
                                <Link key={a.slug} to={`/area/${a.slug}`}>{L.link(a)}</Link>
                            ))}
                            <Link to="/areas">{L.all}</Link>
                        </div>
                    </>
                )}
            </main>

            <Footer />
        </div>
    );
}
